/* eslint-disable react-refresh/only-export-components */
import { lazy } from 'react';

import { PublicRoutesWrapper } from 'pages/public/PublicRoutes';
import { ROUTE } from 'pages/public/Routes';
import {
  AICallerIcon,
  AutomationIcon,
  CalendarIcon,
  HomeIcon,
  ImportExportIcon,
  InboxIcon,
  ProfileIcon,
  ReportIcon,
  SalesIcon,
  SettingIcon,
  StackIcon,
  ToadIcon,
  WebsiteGlobeIcon,
} from 'ui/components/icon';
import { IoLibrary } from 'react-icons/io5';
import c, { FEATURE_FLAG_KEYS } from 'utils/constants';
import { IoMdHelpCircle } from 'react-icons/io';

import Logout from '../Logout';

const Onboarding = lazy(() => import('./containers/onboarding'));
const Verify = lazy(() => import('./containers/verify'));
const Preview = lazy(() => import('./containers/preview'));

const AdminAccounts = lazy(() => import('modules/admin/accounts'));
const Leads = lazy(() => import('modules/account/contact/containers/Leads'));
const Companies = lazy(
  () => import('modules/account/contact/containers/Companies'),
);

const ToadAI = lazy(() => import('modules/account/copilot/containers/ToadAI'));

const Contact = lazy(() => import('modules/account/contact'));
const Data = lazy(() => import('modules/account/data'));
const Resource = lazy(() => import('modules/account/resource'));
const ResourceTemplates = lazy(
  () => import('modules/account/resource/containers/Templates'),
);
const Sales = lazy(() => import('modules/account/sales'));
const Calendar = lazy(() => import('modules/account/calendar'));
const Dashboard = lazy(() => import('modules/account/dashboard'));
const Automation = lazy(() => import('modules/account/automation'));

const Settings = lazy(() => import('modules/account/settings'));
const Notifications = lazy(() => import('modules/account/notifications'));
const Inbox = lazy(() => import('modules/account/inbox'));
const WebsiteBuilder = lazy(() => import('modules/account/websiteBuilder'));

const AdminDashboard = lazy(() => import('modules/admin/dashboard'));

const AdminFeature = lazy(() => import('modules/admin/featureFlags'));

const AdminHelp = lazy(
  () => import('modules/admin/help/containers/TicketsScreen'),
);
const AccountHelp = lazy(
  () => import('modules/account/help/containers/TicketsScreen'),
);

const Reports = lazy(() => import('modules/account/reports/containers'));

const AICaller = lazy(() => import('modules/account/ai-caller/containers'));

export const DEVELOPMENT_ROUTES: ROUTE[] = [];

export const ACCOUNT_ROUTES: readonly ROUTE[] = [
  {
    index: true,
    path: '/',
    label: 'Home',
    iconLeft: HomeIcon,
    element: () => <Dashboard />,
  },
  {
    path: '/contact',
    to: '/contact/*',
    label: 'Contact',
    iconLeft: ProfileIcon,
    acl: [c.PERMISSION_KEYS.LEADS, c.PERMISSION_KEYS.COMPANIES] as string[],
    element: () => <Contact />,
    subMenu: [
      {
        label: 'Leads',
        path: '/contact/leads',
        childPath: 'leads/*',
        acl: [c.PERMISSION_KEYS.LEADS] as string[],
        element: () => <Leads />,
      },
      {
        label: 'Companies',
        path: '/contact/companies',
        childPath: 'companies/*',
        acl: [c.PERMISSION_KEYS.COMPANIES] as string[],
        element: () => <Companies />,
      },
    ] as ROUTE[],
  },
  {
    path: '/inbox',
    to: '/inbox/*',
    label: 'Inbox',
    iconLeft: InboxIcon,
    element: () => <Inbox />,
    key: FEATURE_FLAG_KEYS.EMAIL_MODULE,
  },
  {
    path: '/sales',
    to: '/sales/*',
    label: 'Sales',
    iconLeft: SalesIcon,
    acl: [
      c.PERMISSION_KEYS.DEALS,
      c.PERMISSION_KEYS.ORDERS,
      c.PERMISSION_KEYS.PRODUCTS,
      c.PERMISSION_KEYS.TASKS,
    ] as string[],
    element: () => <Sales />,
    subMenu: [
      {
        label: 'Deals',
        path: '/sales/deals',
        acl: [c.PERMISSION_KEYS.DEALS] as string[],
      },
      {
        label: 'Invoice',
        path: '/sales/invoice',
        acl: [c.PERMISSION_KEYS.ORDERS] as string[],
      },
      {
        label: 'Products',
        path: '/sales/products',
        acl: [c.PERMISSION_KEYS.PRODUCTS] as string[],
      },
      {
        label: 'Tasks',
        path: '/sales/tasks',
        acl: [c.PERMISSION_KEYS.TASKS] as string[],
      },
    ] as ROUTE[],
  },
  {
    path: '/calendar',
    to: '/calendar/*',
    label: 'Calendar',
    iconLeft: CalendarIcon,
    element: () => <Calendar />,
    acl: [c.PERMISSION_KEYS.EVENTS, c.PERMISSION_KEYS.BOOKINGS] as string[],
    subMenu: [
      {
        label: 'Calendar',
        path: '/calendar/calendar',
        acl: [c.PERMISSION_KEYS.EVENTS] as string[],
      },
      {
        label: 'Bookings',
        path: '/calendar/bookings',
        acl: [c.PERMISSION_KEYS.BOOKINGS] as string[],
      },
    ] as ROUTE[],
  },
  {
    path: '/automation',
    to: '/automation/*',
    label: 'Automation',
    iconLeft: AutomationIcon,
    element: () => <Automation />,
    key: FEATURE_FLAG_KEYS.AUTOMATION_MODULE,
    acl: [c.PERMISSION_KEYS.AUTOMATION] as string[],
  },
  {
    path: '/ai-caller',
    to: '/ai-caller/*',
    label: 'AI Caller',
    iconLeft: AICallerIcon,
    element: () => <AICaller />,
    acl: [
      c.PERMISSION_KEYS.ACTIVE_AGENTS,
      c.PERMISSION_KEYS.CAMPAIGNS,
    ] as string[],
    key: FEATURE_FLAG_KEYS.ACTIVE_AGENT_MODULE,
    subMenu: [
      {
        label: 'Dashboard',
        path: '/ai-caller/dashboard',
        acl: [c.PERMISSION_KEYS.ACTIVE_AGENTS] as string[],
      },
      {
        label: 'Campaigns',
        path: '/ai-caller/campaigns',
        acl: [c.PERMISSION_KEYS.CAMPAIGNS] as string[],
      },
      {
        label: 'Call Logs',
        path: '/ai-caller/logs',
        acl: [c.PERMISSION_KEYS.ACTIVE_AGENTS] as string[],
      },
      {
        label: 'Settings',
        path: '/ai-caller/settings',
        acl: [c.PERMISSION_KEYS.ACTIVE_AGENTS] as string[],
      },
    ] as ROUTE[],
    isSubscribe: true,
    isLocked: true,
  },
  {
    path: '/notifications',
    to: '/notifications',
    element: () => <Notifications />,
    hidden: true,
  },
  {
    path: '/data',
    to: '/data/*',
    label: 'Import and Export',
    iconLeft: ImportExportIcon,
    element: () => <Data />,
    atFooter: true,
  },
  {
    path: '/settings',
    to: '/settings/*',
    label: 'Settings',
    iconLeft: SettingIcon,
    element: () => <Settings />,
    atFooter: true,
  },
  {
    path: '/resource',
    to: '/resource/*',
    label: 'Resource',
    iconLeft: IoLibrary,
    element: () => <Resource />,
    subMenu: [
      {
        label: 'Templates',
        path: '/resource/templates',
        childPath: 'templates/*',
        // acl: [c.PERMISSION_KEYS.LEADS] as string[],
        element: () => <ResourceTemplates />,
      },
    ] as ROUTE[],
  },
  {
    path: '/help',
    to: '/help',
    label: 'Help',
    hidden: true,
    element: () => <AccountHelp />,
  },
  {
    path: '/website-builder',
    label: 'Website',
    key: FEATURE_FLAG_KEYS.WEBSITE_BUILDER_MODULE,
    iconLeft: WebsiteGlobeIcon,
    element: () => <WebsiteBuilder />,
    isSubscribe: true,
    isLocked: true,
  },
  {
    path: '/reports',
    to: '/reports/*',
    label: 'Reports',
    element: () => <Reports />,
    iconLeft: ReportIcon,
  },
  {
    path: '/toadai',
    to: '/toadai/*',
    label: 'Toad AI',
    key: FEATURE_FLAG_KEYS.TOAD_MODULE,
    iconLeft: ToadIcon,
    element: () => <ToadAI />,
    isSubscribe: true,
    isLocked: true,
  },
  {
    path: '/notifications',
    to: '/notifications',
    element: () => <Notifications />,
    hidden: true,
  },
  ...DEVELOPMENT_ROUTES,
] as const;

export const ADMIN_ROUTES: ROUTE[] = [
  {
    index: true,
    path: '/',
    label: 'Home',
    iconLeft: HomeIcon,
    element: () => <AdminDashboard />,
  },
  {
    path: '/accounts',
    to: '/accounts/*',
    label: 'Accounts',
    iconLeft: ProfileIcon,
    element: () => <AdminAccounts />,
  },
  {
    path: '/app-versions',
    to: '/app-versions/*',
    label: 'Version Manager',
    iconLeft: StackIcon,
    element: () => <AdminFeature />,
    subMenu: [
      {
        label: 'App Versions',
        path: '/app-versions/features',
      },
      {
        label: 'Flags',
        path: '/app-versions/flags',
      },
    ] as ROUTE[],
  },
  {
    path: '/admin/help',
    to: '/admin/help',
    label: 'Customer Support',
    iconLeft: IoMdHelpCircle,
    element: () => <AdminHelp />,
  },
  {
    path: '/preview/accounts/:accountId/users/:accountUserId',
    to: '/preview/accounts/:accountId/users/:accountUserId/*',
    element: () => <Preview />,
    hidden: true,
  },
  {
    path: '/notifications',
    to: '/notifications',
    element: () => <Notifications />,
    hidden: true,
  },
  ...DEVELOPMENT_ROUTES,
];

export const COMMON_ROUTES: ROUTE[] = [
  {
    path: '/verify/*',
    element: () => <Verify />,
    isCommon: true,
  },
  {
    path: '/p/*',
    element: () => <PublicRoutesWrapper />,
    isCommon: true,
  },
  {
    path: '/logout',
    element: () => <Logout />,
    isCommon: true,
  },
  {
    path: '/onboarding/*',
    element: () => <Onboarding />,
    isCommon: true,
  },
];
